import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api/apiClient';
import PageHeader from '../components/PageHeader';
import Modal from '../components/Modal';
import SortFilter, { sortItems } from '../components/SortFilter';

const COUNTRY_CODES = [
  { code: '+91', label: '🇮🇳 +91', name: 'India' },
  { code: '+27', label: '🇿🇦 +27', name: 'South Africa' },
  { code: '+1', label: '🇺🇸 +1', name: 'USA' },
  { code: '+44', label: '🇬🇧 +44', name: 'UK' },
  { code: '+971', label: '🇦🇪 +971', name: 'UAE' },
  { code: '+61', label: '🇦🇺 +61', name: 'Australia' },
  { code: '+65', label: '🇸🇬 +65', name: 'Singapore' },
  { code: '+966', label: '🇸🇦 +966', name: 'Saudi Arabia' },
];

const emptyForm = {
  name: '',
  address: '',
  city: '',
  country_code: '+91',
  phone_number: '',
  work_days: 'Mon - Fri',
  start_time: '09:00',
  end_time: '18:00',
  is_active: true,
};

function parsePhoneString(fullPhone) {
  if (!fullPhone) return { country_code: '+91', phone_number: '' };

  const cleaned = String(fullPhone).trim();
  const sortedCodes = [...COUNTRY_CODES].sort((a, b) => b.code.length - a.code.length);
  for (const cc of sortedCodes) {
    if (cleaned.startsWith(cc.code)) {
      const digitsOnly = cleaned.slice(cc.code.length).replace(/\D/g, '').slice(0, 10);
      return { country_code: cc.code, phone_number: digitsOnly };
    }
  }

  // Fallback: extract last 10 digits if possible
  const digitsOnly = cleaned.replace(/\D/g, '').slice(-10);
  return { country_code: '+91', phone_number: digitsOnly };
}

function time12To24(time12) {
  if (!time12) return '';
  const match = time12.trim().match(/^(\d{1,2}):?(\d{2})?\s*(AM|PM|am|pm)?$/i);
  if (!match) return '';
  let hours = parseInt(match[1], 10);
  const minutes = match[2] || '00';
  const period = match[3] ? match[3].toUpperCase() : null;
  if (period === 'PM' && hours < 12) hours += 12;
  if (period === 'AM' && hours === 12) hours = 0;
  return `${String(hours).padStart(2, '0')}:${minutes}`;
}

function time24To12(time24) {
  if (!time24) return '';
  const [hStr, mStr] = time24.split(':');
  let hours = parseInt(hStr, 10);
  if (isNaN(hours)) return '';
  const period = hours >= 12 ? 'PM' : 'AM';
  hours = hours % 12 || 12;
  return `${String(hours).padStart(2, '0')}:${mStr || '00'} ${period}`;
}

function parseWorkingHours(str) {
  if (!str) return { start_time: '09:00', end_time: '18:00' };

  let timePart = str;
  if (str.includes(',')) {
    timePart = str.split(',')[1].trim();
  }

  const times = timePart.split(/[-–—to]/i);
  let start_time = '09:00';
  let end_time = '18:00';

  if (times[0]) {
    const parsedStart = time12To24(times[0].trim());
    if (parsedStart) start_time = parsedStart;
  }
  if (times[1]) {
    const parsedEnd = time12To24(times[1].trim());
    if (parsedEnd) end_time = parsedEnd;
  }

  return { start_time, end_time };
}

function buildWorkingHoursRange(start_time, end_time) {
  if (!start_time && !end_time) return '';
  const start12 = start_time ? time24To12(start_time) : '';
  const end12 = end_time ? time24To12(end_time) : '';
  return start12 && end12 ? `${start12} - ${end12}` : (start12 || end12);
}

export default function BranchesPage() {
  const navigate = useNavigate();

  const [branches, setBranches] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [sortOption, setSortOption] = useState('a-z');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [form, setForm] = useState(emptyForm);
  const [editingBranch, setEditingBranch] = useState(null);
  const [assignment, setAssignment] = useState({ teacher_id: '', branch_id: '' });
  const [teacherSearch, setTeacherSearch] = useState('');
  const [isTeacherMenuOpen, setIsTeacherMenuOpen] = useState(false);
  const [branchSearch, setBranchSearch] = useState('');
  const [isBranchMenuOpen, setIsBranchMenuOpen] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Classes modal state
  const [isClassesModalOpen, setIsClassesModalOpen] = useState(false);
  const [selectedBranchForClasses, setSelectedBranchForClasses] = useState(null);
  const [branchClasses, setBranchClasses] = useState([]);
  const [classForm, setClassForm] = useState({ name: '', section: '', capacity: 30 });
  const [classesLoading, setClassesLoading] = useState(false);
  const [classesError, setClassesError] = useState('');
  const [classesSuccess, setClassesSuccess] = useState('');

  // Draft classes for Add Branch modal
  const [initialClassesList, setInitialClassesList] = useState([
    { name: 'Nursery', section: 'A' },
    { name: 'LKG', section: 'A' },
    { name: 'UKG', section: 'A' }
  ]);
  const [newClassNameInput, setNewClassNameInput] = useState('');
  const [newClassSectionInput, setNewClassSectionInput] = useState('');

  const addDraftClass = (name, section = '') => {
    if (!name || !name.trim()) return;
    const cleanName = name.trim();
    const cleanSection = section ? section.trim() : '';
    // Prevent exact duplicate in draft
    if (initialClassesList.some(c => c.name.toLowerCase() === cleanName.toLowerCase() && (c.section || '').toLowerCase() === cleanSection.toLowerCase())) {
      return;
    }
    setInitialClassesList(prev => [...prev, { name: cleanName, section: cleanSection }]);
  };

  const removeDraftClass = (index) => {
    setInitialClassesList(prev => prev.filter((_, i) => i !== index));
  };


  useEffect(() => {
    fetchBranches();
  }, []);

  async function fetchClasses(branchId) {
    setClassesLoading(true);
    setClassesError('');
    try {
      const res = await api.get(`/branches/${branchId}/classes`);
      setBranchClasses(res.data.data || []);
    } catch (err) {
      setClassesError(err.response?.data?.message || 'Unable to load branch classes');
    } finally {
      setClassesLoading(false);
    }
  }

  const openClassesModal = (branch) => {
    setSelectedBranchForClasses(branch);
    setClassForm({ name: '', section: '', capacity: 30 });
    setClassesError('');
    setClassesSuccess('');
    setIsClassesModalOpen(true);
    fetchClasses(branch.id);
  };

  const closeClassesModal = () => {
    setIsClassesModalOpen(false);
    setSelectedBranchForClasses(null);
    setBranchClasses([]);
  };

  const handleAddClass = async (e) => {
    e.preventDefault();
    const cleanName = classForm.name.trim();
    const cleanSection = classForm.section ? classForm.section.trim() : '';

    if (!cleanName) {
      setClassesError('Class name is required');
      return;
    }

    // Client-side duplicate check
    const isDuplicate = branchClasses.some(
      cls => (cls.name || '').trim().toLowerCase() === cleanName.toLowerCase() &&
             (cls.section || '').trim().toLowerCase() === cleanSection.toLowerCase()
    );

    if (isDuplicate) {
      setClassesError(`Class "${cleanName}"${cleanSection ? ` Section "${cleanSection}"` : ''} already exists in this branch.`);
      return;
    }

    setClassesError('');
    setClassesSuccess('');
    try {
      await api.post(`/branches/${selectedBranchForClasses.id}/classes`, {
        name: cleanName,
        section: cleanSection || null,
        capacity: classForm.capacity ? parseInt(classForm.capacity) : 30,
      });
      setClassesSuccess(`Class "${cleanName}" created successfully`);
      setClassForm({ name: '', section: '', capacity: 30 });
      fetchClasses(selectedBranchForClasses.id);
    } catch (err) {
      setClassesError(err.response?.data?.message || 'Unable to add class');
    }
  };

  const handleMoveClass = async (index, direction) => {
    if (direction === 'up' && index === 0) return;
    if (direction === 'down' && index === branchClasses.length - 1) return;

    const targetIndex = direction === 'up' ? index - 1 : index + 1;
    const newClasses = [...branchClasses];
    
    // Swap items in array
    const temp = newClasses[index];
    newClasses[index] = newClasses[targetIndex];
    newClasses[targetIndex] = temp;

    // Optimistic state update
    setBranchClasses(newClasses);

    try {
      const orderedIds = newClasses.map(c => c.id);
      await api.put('/branches/classes/reorder', { orderedIds });
    } catch (err) {
      setClassesError(err.response?.data?.message || 'Failed to update class order');
      fetchClasses(selectedBranchForClasses.id);
    }
  };

  const handleToggleClassStatus = async (cls) => {
    setClassesError('');
    setClassesSuccess('');
    try {
      const newStatus = cls.is_active === 1 ? 0 : 1;
      await api.put(`/branches/classes/${cls.id}`, { is_active: newStatus });
      setClassesSuccess(`Class "${cls.name}" ${newStatus === 1 ? 'activated' : 'deactivated'}`);
      fetchClasses(selectedBranchForClasses.id);
    } catch (err) {
      setClassesError(err.response?.data?.message || 'Unable to update class status');
    }
  };


  async function fetchBranches() {
    setLoading(true);
    setError('');
    try {
      const [branchResponse, teacherResponse] = await Promise.all([
        api.get('/admin/branches'),
        api.get('/users', { params: { role: 'teacher' } }),
      ]);
      setBranches(branchResponse.data.data || []);
      setTeachers(teacherResponse.data.data || []);
    } catch (err) {
      setError(err.response?.data?.message || 'Unable to load branches');
    } finally {
      setLoading(false);
    }
  }

  const handleChange = (event) => {
    const { name, value, type, checked } = event.target;
    setForm((prev) => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  const openAddModal = () => {
    setEditingBranch(null);
    setForm(emptyForm);
    setInitialClassesList([
      { name: 'Nursery', section: 'A' },
      { name: 'LKG', section: 'A' },
      { name: 'UKG', section: 'A' }
    ]);
    setNewClassNameInput('');
    setNewClassSectionInput('');
    setIsModalOpen(true);
  };

  const openEditModal = async (branch) => {
    setEditingBranch(branch);
    const parsedHours = parseWorkingHours(branch.working_hours);
    const parsedPhone = parsePhoneString(branch.phone);

    let workDays = branch.working_days || 'Mon - Fri';
    if (!branch.working_days && branch.working_hours && branch.working_hours.includes(',')) {
      workDays = branch.working_hours.split(',')[0].trim();
    }

    setForm({
      name: branch.name || '',
      address: branch.address || '',
      city: branch.city || '',
      country_code: parsedPhone.country_code,
      phone_number: parsedPhone.phone_number,
      work_days: workDays,
      start_time: parsedHours.start_time,
      end_time: parsedHours.end_time,
      is_active: branch.is_active === 1,
    });
    setNewClassNameInput('');
    setNewClassSectionInput('');
    
    try {
      const res = await api.get(`/branches/${branch.id}/classes`);
      const existingClasses = res.data.data || [];
      setInitialClassesList(existingClasses.map(c => ({ name: c.name, section: c.section || '' })));
    } catch (e) {
      setInitialClassesList([]);
    }

    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setEditingBranch(null);
    setForm(emptyForm);
    setInitialClassesList([]);
    setNewClassNameInput('');
    setNewClassSectionInput('');
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError('');
    setSuccessMessage('');

    if (!form.name.trim()) {
      setError('Branch name is required');
      return;
    }

    if (form.phone_number && form.phone_number.length !== 10) {
      setError('Phone number must be exactly 10 digits');
      return;
    }

    const working_hours_str = buildWorkingHoursRange(form.start_time, form.end_time);
    const formattedPhone = form.phone_number ? `${form.country_code}${form.phone_number}` : null;

    try {
      const payload = {
        name: form.name.trim(),
        address: form.address.trim() || null,
        city: form.city.trim() || null,
        phone: formattedPhone,
        working_days: form.work_days || 'Mon - Fri',
        working_hours: working_hours_str || null,
        is_active: form.is_active ? 1 : 0,
        classes: initialClassesList
      };

      if (editingBranch) {
        await api.put(`/admin/branches/${editingBranch.id}`, payload);
        // Also add any new draft classes if editing
        if (initialClassesList.length > 0) {
          for (const cls of initialClassesList) {
            try {
              await api.post(`/branches/${editingBranch.id}/classes`, { name: cls.name, section: cls.section || null });
            } catch (err) {}
          }
        }
        setSuccessMessage('Branch updated successfully');
      } else {
        await api.post('/admin/branches', payload);
        setSuccessMessage('Branch created with classes successfully');
      }

      closeModal();
      fetchBranches();
    } catch (err) {
      setError(err.response?.data?.message || 'Unable to save branch');
    }
  };

  const handleToggleStatus = async (branch) => {
    setError('');
    setSuccessMessage('');
    const newStatus = branch.is_active === 1 ? 0 : 1;
    try {
      await api.put(`/admin/branches/${branch.id}`, { is_active: newStatus });
      setSuccessMessage(`Branch "${branch.name}" is now ${newStatus === 1 ? 'Active' : 'Inactive'}`);
      fetchBranches();
    } catch (err) {
      setError(err.response?.data?.message || 'Unable to update status');
    }
  };

  const handleAssign = async (event) => {
    event.preventDefault();
    setError('');
    setSuccessMessage('');
    try {
      await api.post('/admin/branches/assign-teacher', assignment);
      setSuccessMessage('Teacher assigned to branch');
      setAssignment({ teacher_id: '', branch_id: '' });
      setTeacherSearch('');
      setBranchSearch('');
      fetchBranches();
    } catch (err) {
      setError(err.response?.data?.message || 'Unable to assign teacher');
    }
  };

  const sortedBranches = sortItems(branches, sortOption, 'name', 'created_at');

  const renderWorkingHours = (branch) => {
    if (branch.working_days && branch.working_hours) {
      return `${branch.working_days}, ${branch.working_hours}`;
    }
    if (branch.working_days) return branch.working_days;
    return branch.working_hours || '-';
  };

  return (
    <div>
      <PageHeader
        title="Branch Management"
        subtitle="Create branches, set working hours, and assign teachers"
        action={
          <button type="button" className="primary-button" onClick={openAddModal} style={{ whiteSpace: 'nowrap' }}>
            + Add Branch
          </button>
        }
      />

      {successMessage && <div className="success-message">{successMessage}</div>}
      {error && <div className="error-message">{error}</div>}

      <section className="panel-section">
        <h3 style={{ margin: '0 0 12px', fontSize: 16, fontWeight: 700, color: '#1e293b' }}>Assign teacher to branch</h3>
        <form onSubmit={handleAssign} style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          {/* Searchable Teacher Selector */}
          <div style={{ position: 'relative', minWidth: 250 }}>
            <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
              <input
                type="text"
                placeholder="Search or select teacher..."
                value={teacherSearch}
                onFocus={() => setIsTeacherMenuOpen(true)}
                onChange={(e) => {
                  setTeacherSearch(e.target.value);
                  setAssignment(p => ({ ...p, teacher_id: '' }));
                  setIsTeacherMenuOpen(true);
                }}
                required={!assignment.teacher_id}
                style={{
                  padding: '8px 30px 8px 12px',
                  borderRadius: 8,
                  border: '1px solid #cbd5e1',
                  fontSize: 14,
                  width: '100%',
                  outline: 'none',
                  background: '#ffffff',
                  boxSizing: 'border-box'
                }}
              />
              {assignment.teacher_id || teacherSearch ? (
                <button
                  type="button"
                  onClick={() => {
                    setTeacherSearch('');
                    setAssignment(p => ({ ...p, teacher_id: '' }));
                  }}
                  style={{
                    position: 'absolute',
                    right: 8,
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    color: '#94a3b8',
                    fontSize: 14,
                    fontWeight: 700
                  }}
                >
                  ×
                </button>
              ) : (
                <span style={{ position: 'absolute', right: 10, color: '#94a3b8', fontSize: 11, pointerEvents: 'none' }}>
                  ▼
                </span>
              )}
            </div>

            {/* Dropdown Menu */}
            {isTeacherMenuOpen && (
              <>
                <div
                  style={{ position: 'fixed', inset: 0, zIndex: 40 }}
                  onClick={() => setIsTeacherMenuOpen(false)}
                />
                <div style={{
                  position: 'absolute',
                  top: '100%',
                  left: 0,
                  right: 0,
                  marginTop: 4,
                  maxHeight: 220,
                  overflowY: 'auto',
                  background: '#ffffff',
                  border: '1px solid #cbd5e1',
                  borderRadius: 8,
                  boxShadow: '0 10px 25px rgba(0,0,0,0.15)',
                  zIndex: 50
                }}>
                  {teachers.filter(t => {
                    if (!teacherSearch.trim()) return true;
                    const q = teacherSearch.toLowerCase().trim();
                    return (t.name || '').toLowerCase().includes(q) ||
                           (t.phone || '').toLowerCase().includes(q) ||
                           (t.email || '').toLowerCase().includes(q);
                  }).length === 0 ? (
                    <div style={{ padding: '10px 14px', fontSize: 13, color: '#94a3b8' }}>
                      No teacher found matching "{teacherSearch}"
                    </div>
                  ) : (
                    teachers.filter(t => {
                      if (!teacherSearch.trim()) return true;
                      const q = teacherSearch.toLowerCase().trim();
                      return (t.name || '').toLowerCase().includes(q) ||
                             (t.phone || '').toLowerCase().includes(q) ||
                             (t.email || '').toLowerCase().includes(q);
                    }).map((teacher) => (
                      <div
                        key={teacher.id}
                        onClick={() => {
                          setAssignment(p => ({ ...p, teacher_id: teacher.id }));
                          setTeacherSearch(teacher.name);
                          setIsTeacherMenuOpen(false);
                        }}
                        style={{
                          padding: '8px 12px',
                          fontSize: 14,
                          cursor: 'pointer',
                          borderBottom: '1px solid #f1f5f9',
                          background: String(assignment.teacher_id) === String(teacher.id) ? '#eff6ff' : '#ffffff',
                          color: String(assignment.teacher_id) === String(teacher.id) ? '#2563eb' : '#1e293b',
                          fontWeight: String(assignment.teacher_id) === String(teacher.id) ? 600 : 400
                        }}
                        onMouseEnter={(e) => {
                          if (String(assignment.teacher_id) !== String(teacher.id)) {
                            e.currentTarget.style.background = '#f8fafc';
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (String(assignment.teacher_id) !== String(teacher.id)) {
                            e.currentTarget.style.background = '#ffffff';
                          }
                        }}
                      >
                        <div style={{ fontWeight: 600 }}>{teacher.name}</div>
                        {(teacher.phone || teacher.email) && (
                          <div style={{ fontSize: 12, color: '#64748b' }}>
                            {teacher.phone || teacher.email}
                          </div>
                        )}
                      </div>
                    ))
                  )}
                </div>
              </>
            )}
          </div>
          {/* Searchable Branch Selector */}
          <div style={{ position: 'relative', minWidth: 250 }}>
            <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
              <input
                type="text"
                placeholder="Search or select branch..."
                value={branchSearch}
                onFocus={() => setIsBranchMenuOpen(true)}
                onChange={(e) => {
                  setBranchSearch(e.target.value);
                  setAssignment(p => ({ ...p, branch_id: '' }));
                  setIsBranchMenuOpen(true);
                }}
                required={!assignment.branch_id}
                style={{
                  padding: '8px 30px 8px 12px',
                  borderRadius: 8,
                  border: '1px solid #cbd5e1',
                  fontSize: 14,
                  width: '100%',
                  outline: 'none',
                  background: '#ffffff',
                  boxSizing: 'border-box'
                }}
              />
              {assignment.branch_id || branchSearch ? (
                <button
                  type="button"
                  onClick={() => {
                    setBranchSearch('');
                    setAssignment(p => ({ ...p, branch_id: '' }));
                  }}
                  style={{
                    position: 'absolute',
                    right: 8,
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    color: '#94a3b8',
                    fontSize: 14,
                    fontWeight: 700
                  }}
                >
                  ×
                </button>
              ) : (
                <span style={{ position: 'absolute', right: 10, color: '#94a3b8', fontSize: 11, pointerEvents: 'none' }}>
                  ▼
                </span>
              )}
            </div>

            {/* Dropdown Menu */}
            {isBranchMenuOpen && (
              <>
                <div
                  style={{ position: 'fixed', inset: 0, zIndex: 40 }}
                  onClick={() => setIsBranchMenuOpen(false)}
                />
                <div style={{
                  position: 'absolute',
                  top: '100%',
                  left: 0,
                  right: 0,
                  marginTop: 4,
                  maxHeight: 220,
                  overflowY: 'auto',
                  background: '#ffffff',
                  border: '1px solid #cbd5e1',
                  borderRadius: 8,
                  boxShadow: '0 10px 25px rgba(0,0,0,0.15)',
                  zIndex: 50
                }}>
                  {branches.filter(b => {
                    if (!branchSearch.trim()) return true;
                    const q = branchSearch.toLowerCase().trim();
                    return (b.name || '').toLowerCase().includes(q) ||
                           (b.city || '').toLowerCase().includes(q) ||
                           (b.address || '').toLowerCase().includes(q);
                  }).length === 0 ? (
                    <div style={{ padding: '10px 14px', fontSize: 13, color: '#94a3b8' }}>
                      No branch found matching "{branchSearch}"
                    </div>
                  ) : (
                    branches.filter(b => {
                      if (!branchSearch.trim()) return true;
                      const q = branchSearch.toLowerCase().trim();
                      return (b.name || '').toLowerCase().includes(q) ||
                             (b.city || '').toLowerCase().includes(q) ||
                             (b.address || '').toLowerCase().includes(q);
                    }).map((branch) => (
                      <div
                        key={branch.id}
                        onClick={() => {
                          setAssignment(p => ({ ...p, branch_id: branch.id }));
                          setBranchSearch(branch.name);
                          setIsBranchMenuOpen(false);
                        }}
                        style={{
                          padding: '8px 12px',
                          fontSize: 14,
                          cursor: 'pointer',
                          borderBottom: '1px solid #f1f5f9',
                          background: String(assignment.branch_id) === String(branch.id) ? '#eff6ff' : '#ffffff',
                          color: String(assignment.branch_id) === String(branch.id) ? '#2563eb' : '#1e293b',
                          fontWeight: String(assignment.branch_id) === String(branch.id) ? 600 : 400
                        }}
                        onMouseEnter={(e) => {
                          if (String(assignment.branch_id) !== String(branch.id)) {
                            e.currentTarget.style.background = '#f8fafc';
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (String(assignment.branch_id) !== String(branch.id)) {
                            e.currentTarget.style.background = '#ffffff';
                          }
                        }}
                      >
                        <div style={{ fontWeight: 600 }}>{branch.name}</div>
                        {(branch.city || branch.address) && (
                          <div style={{ fontSize: 12, color: '#64748b' }}>
                            {branch.city ? `${branch.city}${branch.address ? ' - ' + branch.address : ''}` : branch.address}
                          </div>
                        )}
                      </div>
                    ))
                  )}
                </div>
              </>
            )}
          </div>
          <button type="submit" className="primary-button" style={{ padding: '8px 24px', whiteSpace: 'nowrap' }}>Assign</button>
        </form>
      </section>

      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', marginBottom: 16 }}>
        <SortFilter sortOption={sortOption} onSortChange={setSortOption} />
      </div>

      {loading ? (
        <div>Loading branches...</div>
      ) : branches.length === 0 ? (
        <div>No branches found.</div>
      ) : (
        <div className="table-container">
          <table>
            <thead>
              <tr>
                <th>Name</th>
                <th>Address</th>
                <th>City</th>
                <th>Phone</th>
                <th>Working hours</th>
                <th>Teachers</th>
                <th>Students</th>
                <th>Status</th>
                <th style={{ textAlign: 'center' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {sortedBranches.map((branch) => (
                <tr key={branch.id}>
                  <td>{branch.name || '-'}</td>
                  <td>{branch.address || '-'}</td>
                  <td>{branch.city || '-'}</td>
                  <td>{branch.phone || '-'}</td>
                  <td>{renderWorkingHours(branch)}</td>
                  <td
                    onClick={() => navigate(`/teachers?branch_id=${branch.id}`)}
                    style={{
                      cursor: branch.teacher_count > 0 ? 'pointer' : 'default',
                      textDecoration: branch.teacher_count > 0 ? 'underline' : 'none',
                      textDecorationColor: '#6b7280'
                    }}
                  >
                    {branch.teacher_count || 0}
                  </td>
                  <td
                    onClick={() => navigate(`/students?branch_id=${branch.id}`)}
                    style={{
                      cursor: branch.student_count > 0 ? 'pointer' : 'default',
                      textDecoration: branch.student_count > 0 ? 'underline' : 'none',
                      textDecorationColor: '#6b7280'
                    }}
                  >
                    {branch.student_count || 0}
                  </td>
                  <td>
                    <button
                      type="button"
                      onClick={() => handleToggleStatus(branch)}
                      title={`Click to set as ${branch.is_active === 1 ? 'Inactive' : 'Active'}`}
                      style={{
                        border: 'none',
                        borderRadius: '16px',
                        padding: '4px 12px',
                        fontSize: '12px',
                        fontWeight: 600,
                        cursor: 'pointer',
                        backgroundColor: branch.is_active === 1 ? '#dcfce7' : '#fee2e2',
                        color: branch.is_active === 1 ? '#15803d' : '#b91c1c',
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '6px',
                        transition: 'all 0.2s ease'
                      }}
                    >
                      <span style={{
                        width: '7px',
                        height: '7px',
                        borderRadius: '50%',
                        backgroundColor: branch.is_active === 1 ? '#22c55e' : '#ef4444'
                      }} />
                      {branch.is_active === 1 ? 'Active' : 'Inactive'}
                    </button>
                  </td>
                  <td style={{ textAlign: 'center', whiteSpace: 'nowrap' }}>
                    <div style={{ display: 'flex', gap: 6, justifyContent: 'center', alignItems: 'center', flexWrap: 'nowrap' }}>
                      <button
                        type="button"
                        className="primary-button"
                        style={{ padding: '4px 10px', fontSize: '12px', backgroundColor: '#3b82f6', whiteSpace: 'nowrap' }}
                        onClick={() => openClassesModal(branch)}
                      >
                        Classes
                      </button>
                      <button
                        type="button"
                        className="secondary-button"
                        style={{ padding: '4px 10px', fontSize: '12px', whiteSpace: 'nowrap' }}
                        onClick={() => openEditModal(branch)}
                      >
                        Edit
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Classes Management Modal */}
      <Modal
        isOpen={isClassesModalOpen}
        onClose={closeClassesModal}
        title={selectedBranchForClasses ? `Classes - ${selectedBranchForClasses.name}` : 'Manage Classes'}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          {classesSuccess && <div className="success-message">{classesSuccess}</div>}
          {classesError && <div className="error-message">{classesError}</div>}

          <div style={{ fontWeight: 600, fontSize: '15px', borderBottom: '1px solid #e5e7eb', pb: 2 }}>
            Branch Classes List
          </div>

          {classesLoading ? (
            <div>Loading classes...</div>
          ) : branchClasses.length === 0 ? (
            <div style={{ color: '#6b7280', fontSize: '14px', fontStyle: 'italic' }}>
              No classes created yet for this branch. Add one below!
            </div>
          ) : (
            <div className="table-container" style={{ maxHeight: '250px', overflowY: 'auto' }}>
              <table style={{ fontSize: '13px' }}>
                <thead>
                  <tr>
                    <th style={{ width: '55px' }}>ORDER</th>
                    <th>Class Name</th>
                    <th>Section</th>
                    <th>Students</th>
                    <th>Status</th>
                    <th style={{ textAlign: 'center', width: '90px' }}>REORDER</th>
                    <th style={{ textAlign: 'center' }}>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {branchClasses.map((cls, index) => (
                    <tr key={cls.id || index}>
                      <td style={{ fontWeight: 700, color: '#3b82f6' }}>#{index + 1}</td>
                      <td style={{ fontWeight: 600 }}>{cls.name}</td>
                      <td>{cls.section || '-'}</td>
                      <td>{cls.student_count || 0}</td>
                      <td>
                        <span style={{
                          padding: '2px 8px',
                          borderRadius: '12px',
                          fontSize: '11px',
                          fontWeight: 600,
                          backgroundColor: cls.is_active === 1 ? '#dcfce7' : '#fee2e2',
                          color: cls.is_active === 1 ? '#15803d' : '#b91c1c'
                        }}>
                          {cls.is_active === 1 ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td style={{ textAlign: 'center' }}>
                        <div style={{ display: 'inline-flex', gap: '4px', justifyContent: 'center' }}>
                          <button
                            type="button"
                            disabled={index === 0}
                            onClick={() => handleMoveClass(index, 'up')}
                            title="Move Up"
                            style={{
                              padding: '2px 7px',
                              borderRadius: '4px',
                              border: '1px solid #d1d5db',
                              background: index === 0 ? '#f3f4f6' : '#ffffff',
                              color: index === 0 ? '#9ca3af' : '#1f2937',
                              cursor: index === 0 ? 'not-allowed' : 'pointer',
                              fontSize: '11px',
                              fontWeight: 'bold',
                              boxShadow: '0 1px 2px rgba(0,0,0,0.05)'
                            }}
                          >
                            ▲
                          </button>
                          <button
                            type="button"
                            disabled={index === branchClasses.length - 1}
                            onClick={() => handleMoveClass(index, 'down')}
                            title="Move Down"
                            style={{
                              padding: '2px 7px',
                              borderRadius: '4px',
                              border: '1px solid #d1d5db',
                              background: index === branchClasses.length - 1 ? '#f3f4f6' : '#ffffff',
                              color: index === branchClasses.length - 1 ? '#9ca3af' : '#1f2937',
                              cursor: index === branchClasses.length - 1 ? 'not-allowed' : 'pointer',
                              fontSize: '11px',
                              fontWeight: 'bold',
                              boxShadow: '0 1px 2px rgba(0,0,0,0.05)'
                            }}
                          >
                            ▼
                          </button>
                        </div>
                      </td>
                      <td style={{ textAlign: 'center' }}>
                        <button
                          type="button"
                          onClick={() => handleToggleClassStatus(cls)}
                          style={{
                            border: 'none',
                            background: 'transparent',
                            color: cls.is_active === 1 ? '#ef4444' : '#22c55e',
                            cursor: 'pointer',
                            fontSize: '12px',
                            fontWeight: 600
                          }}
                        >
                          {cls.is_active === 1 ? 'Deactivate' : 'Activate'}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Create New Class Form */}
          <form onSubmit={handleAddClass} style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '12px',
            marginTop: '12px',
            paddingTop: '12px',
            borderTop: '1px solid #e5e7eb'
          }}>
            <div style={{ fontWeight: 600, fontSize: '14px' }}>Add New Class to Branch</div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
              <div>
                <label style={{ fontSize: '12px', fontWeight: 600, display: 'block', marginBottom: '4px' }}>
                  Class Name *
                </label>
                <input
                  type="text"
                  placeholder="e.g. Nursery, LKG, Grade 1"
                  value={classForm.name}
                  onChange={(e) => setClassForm(prev => ({ ...prev, name: e.target.value }))}
                  required
                />
              </div>
              <div>
                <label style={{ fontSize: '12px', fontWeight: 600, display: 'block', marginBottom: '4px' }}>
                  Section (Optional)
                </label>
                <input
                  type="text"
                  placeholder="e.g. A, B, Rose"
                  value={classForm.section}
                  onChange={(e) => setClassForm(prev => ({ ...prev, section: e.target.value }))}
                />
              </div>
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', marginTop: '8px' }}>
              <button type="button" className="secondary-button" onClick={closeClassesModal}>
                Close
              </button>
              <button type="submit" className="primary-button">
                + Add Class
              </button>
            </div>
          </form>
        </div>
      </Modal>


      <Modal
        isOpen={isModalOpen}
        onClose={closeModal}
        title={editingBranch ? 'Edit Branch' : 'Add New Branch'}
      >
        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          <div className="form-row">
            <label style={{ flex: 1 }}>
              <span style={{ fontSize: '13px', fontWeight: 600, color: '#374151', display: 'block', marginBottom: '6px' }}>
                Branch Name <span style={{ color: '#ef4444' }}>*</span>
              </span>
              <input
                name="name"
                value={form.name}
                onChange={handleChange}
                placeholder="e.g. Lotus Academy Main Campus"
                required
              />
            </label>
            <label style={{ flex: 1 }}>
              <span style={{ fontSize: '13px', fontWeight: 600, color: '#374151', display: 'block', marginBottom: '6px' }}>
                City
              </span>
              <input
                name="city"
                value={form.city}
                onChange={handleChange}
                placeholder="e.g. Indore"
              />
            </label>
          </div>

          <div className="form-row">
            <label style={{ flex: 1 }}>
              <span style={{ fontSize: '13px', fontWeight: 600, color: '#374151', display: 'block', marginBottom: '6px' }}>
                Street Address
              </span>
              <input
                name="address"
                value={form.address}
                onChange={handleChange}
                placeholder="e.g. 123 Vijay Nagar, Near Square"
              />
            </label>

            <label style={{ flex: 1 }}>
              <span style={{ fontSize: '13px', fontWeight: 600, color: '#374151', display: 'block', marginBottom: '6px' }}>
                Phone Number
              </span>
              <div style={{ display: 'flex', gap: '8px' }}>
                <select
                  name="country_code"
                  value={form.country_code}
                  onChange={handleChange}
                  style={{ width: '105px', flexShrink: 0, padding: '10px 6px', fontSize: '13px', backgroundColor: '#f8fafc' }}
                >
                  {COUNTRY_CODES.map((c) => (
                    <option key={c.code} value={c.code}>
                      {c.label}
                    </option>
                  ))}
                </select>
                <input
                  type="tel"
                  name="phone_number"
                  value={form.phone_number}
                  onChange={(e) => {
                    const onlyNums = e.target.value.replace(/\D/g, '').slice(0, 10);
                    setForm((prev) => ({ ...prev, phone_number: onlyNums }));
                  }}
                  placeholder="10-digit mobile number"
                  maxLength={10}
                  style={{ flex: 1 }}
                />
              </div>
              {form.phone_number && form.phone_number.length < 10 && (
                <span style={{ fontSize: '11px', color: '#f59e0b', marginTop: '4px', display: 'block', fontWeight: 500 }}>
                  ⚠️ {10 - form.phone_number.length} digits remaining (10 digits required)
                </span>
              )}
              {form.phone_number && form.phone_number.length === 10 && (
                <span style={{ fontSize: '11px', color: '#10b981', marginTop: '4px', display: 'block', fontWeight: 500 }}>
                  ✓ Valid 10-digit mobile number
                </span>
              )}
            </label>
          </div>

          <div style={{
            background: '#f8fafc',
            border: '1px solid #e2e8f0',
            borderRadius: '12px',
            padding: '16px 20px',
            display: 'flex',
            flexDirection: 'column',
            gap: '12px'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#7c3aed" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10"/>
                <polyline points="12 6 12 12 16 14"/>
              </svg>
              <span style={{ fontWeight: 600, fontSize: '14px', color: '#1e293b' }}>Working Schedule</span>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1.2fr 1fr 1fr', gap: '12px' }}>
              <div>
                <span style={{ fontSize: '12px', fontWeight: 600, color: '#475569', display: 'block', marginBottom: '6px' }}>
                  Working Days
                </span>
                <select
                  name="work_days"
                  value={form.work_days}
                  onChange={handleChange}
                  style={{ backgroundColor: '#ffffff' }}
                >
                  <option value="Mon - Fri">Mon - Fri (5 Days)</option>
                  <option value="Mon - Sat">Mon - Sat (6 Days)</option>
                  <option value="Mon - Sun">Mon - Sun (7 Days)</option>
                  <option value="Everyday">Everyday</option>
                </select>
              </div>

              <div>
                <span style={{ fontSize: '12px', fontWeight: 600, color: '#475569', display: 'block', marginBottom: '6px' }}>
                  Opening Time
                </span>
                <input
                  type="time"
                  name="start_time"
                  value={form.start_time}
                  onChange={handleChange}
                  style={{ backgroundColor: '#ffffff' }}
                  required
                />
              </div>

              <div>
                <span style={{ fontSize: '12px', fontWeight: 600, color: '#475569', display: 'block', marginBottom: '6px' }}>
                  Closing Time
                </span>
                <input
                  type="time"
                  name="end_time"
                  value={form.end_time}
                  onChange={handleChange}
                  style={{ backgroundColor: '#ffffff' }}
                  required
                />
              </div>
            </div>

            {(form.start_time || form.end_time) && (
              <div style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '6px',
                fontSize: '12px',
                fontWeight: 600,
                color: '#2563eb',
                background: '#eff6ff',
                border: '1px solid #bfdbfe',
                padding: '6px 12px',
                borderRadius: '8px',
                alignSelf: 'flex-start'
              }}>
                <span>🕒 Schedule Preview:</span>
                <span>{form.work_days}, {buildWorkingHoursRange(form.start_time, form.end_time)}</span>
              </div>
            )}
          </div>

          {/* Initial Branch Classes */}
          <div style={{
            background: '#f8fafc',
            border: '1px solid #e2e8f0',
            borderRadius: '12px',
            padding: '16px 20px',
            display: 'flex',
            flexDirection: 'column',
            gap: '12px'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span style={{ fontSize: '16px' }}>🏫</span>
                <span style={{ fontWeight: 600, fontSize: '14px', color: '#1e293b' }}>Branch Classes</span>
              </div>
              <span style={{ fontSize: '12px', color: '#64748b' }}>Add classes offered at this branch</span>
            </div>

            {/* Presets */}
            <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', alignItems: 'center' }}>
              <span style={{ fontSize: '12px', color: '#64748b', fontWeight: 500 }}>Quick Presets:</span>
              {['Playgroup', 'Nursery', 'LKG', 'UKG', 'Grade 1', 'Grade 2'].map(preset => (
                <button
                  key={preset}
                  type="button"
                  onClick={() => addDraftClass(preset, 'A')}
                  style={{
                    padding: '3px 10px', fontSize: '12px', borderRadius: '12px', border: '1px solid #cbd5e1',
                    background: '#ffffff', color: '#475569', cursor: 'pointer', fontWeight: 500
                  }}
                >
                  + {preset}
                </button>
              ))}
            </div>

            {/* Custom Input */}
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
              <input
                type="text"
                placeholder="Class name (e.g. Grade 3)"
                value={newClassNameInput}
                onChange={(e) => setNewClassNameInput(e.target.value)}
                style={{ flex: 2, backgroundColor: '#ffffff', fontSize: '13px', padding: '8px 10px' }}
              />
              <input
                type="text"
                placeholder="Section (e.g. A)"
                value={newClassSectionInput}
                onChange={(e) => setNewClassSectionInput(e.target.value)}
                style={{ flex: 1, backgroundColor: '#ffffff', fontSize: '13px', padding: '8px 10px' }}
              />
              <button
                type="button"
                className="secondary-button"
                style={{ padding: '8px 14px', fontSize: '13px', whiteSpace: 'nowrap' }}
                onClick={() => {
                  if (newClassNameInput.trim()) {
                    addDraftClass(newClassNameInput.trim(), newClassSectionInput.trim());
                    setNewClassNameInput('');
                    setNewClassSectionInput('');
                  }
                }}
              >
                + Add Class
              </button>
            </div>

            {/* Added Classes Chips */}
            {initialClassesList.length > 0 ? (
              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginTop: '4px' }}>
                {initialClassesList.map((cls, idx) => (
                  <span key={idx} style={{
                    padding: '4px 12px', background: '#e0e7ff', color: '#4338ca', border: '1px solid #c7d2fe',
                    borderRadius: '16px', fontSize: '12px', fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: '6px'
                  }}>
                    {cls.name} {cls.section ? `(${cls.section})` : ''}
                    <button
                      type="button"
                      onClick={() => removeDraftClass(idx)}
                      style={{ border: 'none', background: 'none', color: '#ef4444', cursor: 'pointer', fontWeight: 700, padding: 0, fontSize: '14px' }}
                      title="Remove class"
                    >
                      ×
                    </button>
                  </span>
                ))}
              </div>
            ) : (
              <div style={{ fontSize: '12px', color: '#94a3b8', fontStyle: 'italic' }}>
                No classes added yet. Click presets or type a class name above to add classes.
              </div>
            )}
          </div>

          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            background: '#f8fafc',
            border: '1px solid #e2e8f0',
            borderRadius: '10px',
            padding: '12px 16px'
          }}>
            <div>
              <div style={{ fontSize: '13px', fontWeight: 600, color: '#1e293b' }}>Active Status</div>
              <div style={{ fontSize: '12px', color: '#64748b' }}>Branch will be visible for teacher and student assignments</div>
            </div>
            <label style={{ display: 'inline-flex', alignItems: 'center', cursor: 'pointer', gap: '8px', margin: 0 }}>
              <input
                type="checkbox"
                name="is_active"
                checked={form.is_active}
                onChange={handleChange}
                style={{ width: '18px', height: '18px', cursor: 'pointer', accentColor: '#7c3aed' }}
              />
              <span style={{ fontSize: '13px', fontWeight: 600, color: form.is_active ? '#15803d' : '#b91c1c' }}>
                {form.is_active ? 'Active' : 'Inactive'}
              </span>
            </label>
          </div>

          <div className="modal-actions" style={{ marginTop: '8px' }}>
            <button type="button" className="secondary-button" onClick={closeModal}>
              Cancel
            </button>
            <button
              type="submit"
              className="primary-button"
              style={{ background: 'linear-gradient(90deg, #7c3aed, #06b6d4)', padding: '10px 20px', borderRadius: '8px' }}
            >
              {editingBranch ? 'Update Branch' : 'Save Branch'}
            </button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
